import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconComponent } from '@examdojo/core/icon';
import { adaptEchartsOptions, BaseEchartsDirective, SliderComponent } from '@examdojo/core/widget';
import { ThemeColorName } from '@examdojo/styles';
import { MatTooltip } from '@angular/material/tooltip';
import { EChartsOption } from 'echarts';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, MarkLineComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import katex from 'katex';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import {
  CHART_BAR_BORDER_RADIUS,
  CHART_COLORS,
  CHART_GRID,
  CHART_TOOLTIP_STYLE,
  CHART_X_AXIS_STYLE,
  CHART_Y_AXIS_STYLE,
  fmt,
} from '../../../shared/chart-shared';
import { AnnuityChartParams, AnnuitySliderConfig, TvmField } from './annuity-chart.model';

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

// ---------------------------------------------------------------------------
// Tooltip helpers
// ---------------------------------------------------------------------------

const TIP_LABEL = 'color:var(--neutral-500);font-size:12px';
const TIP_VALUE = 'color:var(--neutral-800);font-size:12px;font-variant-numeric:tabular-nums';

const TIP_DOT = (c: string) =>
  `<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${c};flex-shrink:0"></span>`;

function tipHeader(text: string): string {
  return `<div style="font-size:11px;font-weight:500;color:var(--neutral-500);margin-bottom:6px;letter-spacing:0.03em;text-transform:uppercase">${text}</div>`;
}

function tipColorRow(color: string, label: string, value: string | number): string {
  return `<div style="display:flex;align-items:center;gap:6px;line-height:1.6"><span>${TIP_DOT(
    color,
  )}</span><span style="${TIP_LABEL};flex:1">${label}</span><span style="${TIP_VALUE}">${value}</span></div>`;
}

function tipDivider(): string {
  return `<div style="border-top:1px solid var(--neutral-200);margin:6px 0"></div>`;
}

function tipSummaryRow(label: string, value: string | number): string {
  return `<div style="display:flex;justify-content:space-between;gap:16px;line-height:1.6"><span style="${TIP_LABEL}">${label}</span><span style="font-weight:500;${TIP_VALUE}">${value}</span></div>`;
}

// ---------------------------------------------------------------------------
// TVM helpers
// ---------------------------------------------------------------------------

const DEFAULT_SLIDER_CONFIG: Record<string, AnnuitySliderConfig> = {
  pmt: { min: 10, max: 1000, step: 10 },
  rate: { min: 0.5, max: 15, step: 0.5 },
  periods: { min: 1, max: 120, step: 1 },
  pv: { min: 1000, max: 100000, step: 500 },
  cPerY: { min: 1, max: 12, step: 1 },
};

function computePmt(pv: number, annualRate: number, periods: number, compoundingPerYear: number): number {
  const r = annualRate / (100 * compoundingPerYear);
  if (r === 0) {
    return pv / periods;
  }
  return (pv * r) / (1 - Math.pow(1 + r, -periods));
}

function computeFv(pmt: number, annualRate: number, periods: number, compoundingPerYear: number, pv = 0): number {
  const r = annualRate / (100 * compoundingPerYear);
  if (r === 0) {
    return pv + pmt * periods;
  }
  return pv * Math.pow(1 + r, periods) + pmt * ((Math.pow(1 + r, periods) - 1) / r);
}

interface TvmCell {
  readonly labelHtml: SafeHtml;
  readonly value: number;
  readonly displayValue: string;
  readonly tooltip: string;
  readonly editable: boolean;
  readonly field: TvmField;
  readonly step: number;
  readonly min: number;
  readonly max: number;
  readonly color: ThemeColorName;
  readonly highlight?: boolean;
}

echarts.use([BarChart, LineChart, GridComponent, LegendComponent, MarkLineComponent, TooltipComponent]);

@Component({
  selector: 'dojo-annuity-chart',
  standalone: true,
  imports: [NgxEchartsDirective, SliderComponent, IconComponent, MatTooltip],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './annuity-chart.component.html',
  styleUrl: './annuity-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AnnuityChartComponent extends BaseEchartsDirective {
  constructor() {
    super();
    effect(
      () => {
        const p = this.params();
        this.effectivePmt.set(p.pmt);
        this.effectiveRate.set(p.rate);
        this.effectivePeriods.set(p.periods);
        this.effectivePv.set(p.pv ?? 0);
        this.effectiveCPerY.set(p.compoundingPerYear ?? 12);
      },
      { allowSignalWrites: true },
    );
  }

  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input.required<AnnuityChartParams>();

  private readonly currency = computed(() => this.params().currency ?? '');

  readonly effectivePmt = signal(0);
  readonly effectiveRate = signal(0);
  readonly effectivePeriods = signal(0);
  readonly effectivePv = signal(0);
  readonly effectiveCPerY = signal(12);

  private readonly isInteractive = computed(() => {
    const p = this.params();
    return !!(p.editableFields?.length || p.showSliders);
  });

  readonly resolvedSliderConfig = computed(() => {
    const cfg = this.params().sliderConfig;
    return {
      pmt: { ...DEFAULT_SLIDER_CONFIG['pmt'], ...cfg?.pmt },
      rate: { ...DEFAULT_SLIDER_CONFIG['rate'], ...cfg?.rate },
      periods: { ...DEFAULT_SLIDER_CONFIG['periods'], ...cfg?.periods },
      pv: { ...DEFAULT_SLIDER_CONFIG['pv'], ...cfg?.pv },
      cPerY: { ...DEFAULT_SLIDER_CONFIG['cPerY'], ...cfg?.cPerY },
    };
  });

  readonly computedLoanPmt = computed(() => {
    const p = this.params();
    if (p.mode !== 'loan') {
      return 0;
    }
    const pv = this.isInteractive() ? this.effectivePv() : p.pv ?? 0;
    const rate = this.isInteractive() ? this.effectiveRate() : p.rate;
    const periods = this.isInteractive() ? this.effectivePeriods() : p.periods;
    if (pv === 0) {
      return 0;
    }
    const cPerY = this.isInteractive() ? this.effectiveCPerY() : p.compoundingPerYear ?? 12;
    return computePmt(pv, rate, periods, cPerY);
  });

  readonly chartOptions = computed<EChartsOption>(() => {
    const p = this.params();
    const interactive = this.isInteractive();
    const rate = interactive ? this.effectiveRate() : p.rate;
    const periods = interactive ? this.effectivePeriods() : p.periods;
    const cPerY = interactive ? this.effectiveCPerY() : p.compoundingPerYear ?? 12;
    const effective: AnnuityChartParams = {
      ...p,
      rate,
      periods,
      compoundingPerYear: cPerY,
      explainerMode: p.showSummary ? false : p.explainerMode,
    };

    if (p.mode === 'loan') {
      const pv = interactive ? this.effectivePv() : p.pv ?? 0;
      const loanPmt = interactive ? this.computedLoanPmt() : p.pmt;
      return this.buildLoanChart({ ...effective, pmt: loanPmt, pv });
    }

    const pmt = interactive ? this.effectivePmt() : p.pmt;
    const pv = interactive ? this.effectivePv() : p.pv ?? 0;
    return this.buildSavingChart({ ...effective, pmt, pv });
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  readonly tvmCells = computed<TvmCell[] | null>(() => {
    const p = this.params();
    if (!p.showSummary) {
      return null;
    }

    const interactive = this.isInteractive();
    const editable = new Set<string>(p.editableFields ?? []);
    const rate = interactive ? this.effectiveRate() : p.rate;
    const periods = interactive ? this.effectivePeriods() : p.periods;
    const cPerY = interactive ? this.effectiveCPerY() : p.compoundingPerYear ?? 12;
    const cfg = this.resolvedSliderConfig();

    let pv: number;
    let pmt: number;
    let _fv: number;

    if (p.mode === 'saving') {
      pmt = interactive ? this.effectivePmt() : p.pmt;
      pv = interactive ? this.effectivePv() : p.pv ?? 0;
      _fv = Math.round(computeFv(pmt, rate, periods, cPerY, pv) * 100) / 100;
    } else {
      pv = interactive ? this.effectivePv() : p.pv ?? 0;
      const loanPmt = interactive ? this.computedLoanPmt() : p.pmt;
      pmt = Math.round(loanPmt * 100) / 100;
      _fv = 0;
    }

    const latex = (s: string) => this.sanitizer.bypassSecurityTrustHtml(renderLatex(s));
    const isSaving = p.mode === 'saving';

    const resultField: TvmField = isSaving ? 'FV' : 'PMT';
    const cells: TvmCell[] = [
      {
        labelHtml: latex('PV'),
        value: pv,
        displayValue: this.formatCurrency(pv, true),
        tooltip: isSaving ? 'Present value — starting balance' : 'Present value — initial loan amount',
        editable: editable.has('PV'),
        field: 'PV' as const,
        step: cfg.pv.step,
        min: cfg.pv.min,
        max: cfg.pv.max,
        color: 'pink' as const,
        highlight: pv !== 0,
      },
      {
        labelHtml: latex('I\\%'),
        value: rate,
        displayValue: String(rate),
        tooltip: 'Annual interest rate (%)',
        editable: editable.has('I%'),
        field: 'I%' as const,
        step: cfg.rate.step,
        min: cfg.rate.min,
        max: cfg.rate.max,
        color: 'teal' as const,
      },
      {
        labelHtml: latex('PMT'),
        value: pmt,
        displayValue: this.formatCurrency(pmt, true),
        tooltip: isSaving ? 'Payment made each period into the account' : 'Payment made each period to repay the loan',
        editable: editable.has('PMT'),
        field: 'PMT' as const,
        step: cfg.pmt.step,
        min: cfg.pmt.min,
        max: cfg.pmt.max,
        color: 'neutral' as const,
      },
      {
        labelHtml: latex('N'),
        value: periods,
        displayValue: String(periods),
        tooltip: 'Total number of payment periods',
        editable: editable.has('N'),
        field: 'N' as const,
        step: cfg.periods.step,
        min: cfg.periods.min,
        max: cfg.periods.max,
        color: 'neutral' as const,
      },
      {
        labelHtml: latex('C/Y'),
        value: cPerY,
        displayValue: String(cPerY),
        tooltip: 'Compounding periods per year (changing this adapts N to keep the same duration)',
        editable: editable.has('C/Y'),
        field: 'C/Y' as const,
        step: cfg.cPerY.step,
        min: cfg.cPerY.min,
        max: cfg.cPerY.max,
        color: 'neutral' as const,
      },
    ];

    // Exclude the computed result field from the grid — it's shown in the result box below
    return cells.filter((c) => c.field !== resultField);
  });

  /** Computed result shown prominently below the slider grid (FV for saving, PMT for loan) */
  readonly tvmResult = computed<{
    label: string;
    value: string;
    tooltip: string;
    durHtml: SafeHtml;
    rateHtml: SafeHtml;
  } | null>(() => {
    const p = this.params();
    if (!p.showSummary) {
      return null;
    }
    const interactive = this.isInteractive();
    const rate = interactive ? this.effectiveRate() : p.rate;
    const periods = interactive ? this.effectivePeriods() : p.periods;
    const cPerY = interactive ? this.effectiveCPerY() : p.compoundingPerYear ?? 12;
    const years = periods / cPerY;
    const yearsStr = Number.isInteger(years) ? String(years) : years.toFixed(1);
    const yLabel = years === 1 ? 'year' : 'years';
    const durFormulaHtml = renderLatex(`\\small{\\dfrac{N}{C/Y} = \\dfrac{${periods}}{${cPerY}}}`);
    const durHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<span class="tvm-formula">${durFormulaHtml}</span> <span class="tvm-formula__eq">=</span> <span class="tvm-formula__result">${yearsStr}</span> <span class="tvm-formula__unit">${yLabel}</span>`,
    );
    const monthlyRate = rate / cPerY;
    const monthlyRateStr = Number.isInteger(monthlyRate)
      ? String(monthlyRate)
      : monthlyRate.toFixed(monthlyRate < 1 ? 2 : 1);
    const rateFormulaHtml = renderLatex(`\\small{\\dfrac{I\\%}{C/Y} = \\dfrac{${rate}}{${cPerY}}}`);
    const rateHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<span class="tvm-formula">${rateFormulaHtml}</span> <span class="tvm-formula__eq">=</span> <span class="tvm-formula__result">${monthlyRateStr}%</span> <span class="tvm-formula__unit">per period</span>`,
    );
    const base = { years: yearsStr, durHtml, rateHtml };

    if (p.mode === 'saving') {
      const pmt = interactive ? this.effectivePmt() : p.pmt;
      const pv = interactive ? this.effectivePv() : p.pv ?? 0;
      const fv = Math.round(computeFv(pmt, rate, periods, cPerY, pv) * 100) / 100;
      const fvFormatted = this.formatCurrency(fv, true);
      return {
        ...base,
        label: 'FV',
        value: fvFormatted,
        tooltip: 'Future value — total balance after N periods',
      };
    }

    const loanPmt = interactive ? this.computedLoanPmt() : p.pmt;
    const pmt = Math.round(loanPmt * 100) / 100;
    const pmtFormatted = this.formatCurrency(pmt, true);
    return {
      ...base,
      label: 'PMT',
      value: pmtFormatted,
      tooltip: 'Payment made each period to repay the loan',
    };
  });

  /** Show legacy sliders only when showSliders is set WITHOUT editableFields */
  readonly showLegacySliders = computed(() => {
    const p = this.params();
    return p.showSliders && !p.editableFields?.length;
  });

  onCellEdit(field: TvmField, value: number): void {
    if (isNaN(value) || value < 0) {
      return;
    }
    switch (field) {
      case 'N':
        this.effectivePeriods.set(Math.max(1, Math.round(value)));
        break;
      case 'I%':
        this.effectiveRate.set(Math.max(0, value));
        break;
      case 'PV':
        this.effectivePv.set(Math.max(0, value));
        break;
      case 'PMT':
        this.effectivePmt.set(Math.max(0, value));
        break;
      case 'C/Y': {
        const newCPerY = Math.max(1, Math.round(value));
        const oldCPerY = this.effectiveCPerY();
        const oldN = this.effectivePeriods();
        // Adapt N to keep the same duration: round to whole years first, then multiply
        const years = Math.max(1, Math.round(oldN / oldCPerY));
        this.effectiveCPerY.set(newCPerY);
        this.effectivePeriods.set(years * newCPerY);
        break;
      }
      case 'FV':
        break;
    }
  }

  readonly onPmtChange = (value: number): void => {
    this.effectivePmt.set(value);
  };

  readonly onRateChange = (value: number): void => {
    this.effectiveRate.set(value);
  };

  readonly onPeriodsChange = (value: number): void => {
    this.effectivePeriods.set(value);
  };

  readonly onPvChange = (value: number): void => {
    this.effectivePv.set(value);
  };

  formatCurrency(value: number, includeCurrency = false): string {
    const abs = Math.abs(value);
    const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const prefix = includeCurrency ? this.currency() : '';
    return value < 0 ? `−${prefix}${formatted}` : `${prefix}${formatted}`;
  }

  private buildSavingChart(p: AnnuityChartParams): EChartsOption {
    const { pmt, rate, periods, compoundingPerYear = 12, pv = 0, currency = '' } = p;
    const r = rate / (100 * compoundingPerYear);

    const categories: string[] = [];
    const pvDeposits: number[] = [];
    const pmtDeposits: number[] = [];
    const deposits: number[] = [];
    const interest: number[] = [];

    for (let k = 1; k <= periods; k++) {
      categories.push(String(k));
      const pvGrowth = r > 0 ? pv * Math.pow(1 + r, k) : pv;
      const annuityBalance = r > 0 ? pmt * ((Math.pow(1 + r, k) - 1) / r) : pmt * k;
      const balance = pvGrowth + annuityBalance;
      const totalDeposits = pv + pmt * k;
      pvDeposits.push(pv);
      pmtDeposits.push(pmt * k);
      deposits.push(totalDeposits);
      interest.push(balance - totalDeposits);
    }

    const explainer = p.explainerMode && p.mode === 'saving';

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pvSeries: any =
      pv > 0
        ? {
            type: 'bar',
            name: 'Initial Deposit (PV)',
            stack: 'total',
            data: pvDeposits,
            itemStyle: { color: '#ec4899', borderRadius: [0, 0, 0, 0] },
            barMaxWidth: explainer ? 40 : undefined,
          }
        : null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const depositSeries: any = {
      type: 'bar',
      name: pv > 0 ? 'Regular Deposits' : 'Total Deposits',
      stack: 'total',
      data: explainer
        ? (pv > 0 ? pmtDeposits : deposits).map((val, i) => ({
            value: val,
            itemStyle: {
              color: i === 0 ? '#ec4899' : '#9ca3af',
              borderRadius: [0, 0, 0, 0],
            },
          }))
        : pv > 0
          ? pmtDeposits
          : deposits,
      itemStyle: { color: '#9ca3af', borderRadius: [0, 0, 0, 0] },
      barMaxWidth: explainer ? 40 : undefined,
      emphasis: explainer ? { disabled: true } : undefined,
    };

    if (explainer) {
      const pmtLabel = `+PMT`;
      depositSeries.markLine = {
        silent: true,
        symbol: ['none', 'none'],
        label: { show: false },
        data: [
          ...deposits.slice(0, -1).map((val, i) => [
            {
              xAxis: String(i),
              yAxis: val,
              lineStyle: { color: '#9ca3af', width: 1.5, type: 'dashed' as const },
              label: { show: false },
            },
            { xAxis: String(i + 1), yAxis: val },
          ]),
          ...deposits.slice(1).map((_, i) => [
            {
              xAxis: String(i + 1),
              yAxis: deposits[i],
              symbol: 'none',
              lineStyle: { color: '#14b8a6', width: 2, type: 'dashed' as const },
              label: {
                show: true,
                formatter: pmtLabel,
                color: '#14b8a6',
                fontStyle: 'italic',
                fontSize: 12,
              },
            },
            {
              xAxis: String(i + 1),
              yAxis: deposits[i + 1],
              symbol: 'arrow',
              symbolSize: 8,
            },
          ]),
        ],
      };
    }

    return {
      animation: false,
      tooltip: {
        ...CHART_TOOLTIP_STYLE,
        extraCssText: 'box-shadow:0 4px 14px rgba(0,0,0,0.08);border-radius:8px;',
        padding: [10, 14],
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0, 0, 0, 0.04)' } },
        formatter: (tooltipParams) => {
          const items = tooltipParams as Array<{ dataIndex: number }>;
          if (!items.length) {
            return '';
          }
          const idx = items[0].dataIndex;
          const balance = deposits[idx] + interest[idx];
          const cf = (v: number) => `${currency}${fmt(v)}`;
          return [
            tipHeader(`Period ${categories[idx]}`),
            ...(pv > 0
              ? [
                  tipColorRow('#ec4899', 'Initial Deposit (PV)', cf(pv)),
                  tipColorRow('#9ca3af', 'Regular Deposits', cf(pmt * (idx + 1))),
                ]
              : [tipColorRow('#9ca3af', 'Total Deposits', cf(deposits[idx]))]),
            tipColorRow('#14b8a6', 'Interest Earned', cf(interest[idx])),
            tipDivider(),
            tipSummaryRow('Account Balance', cf(balance)),
          ].join('');
        },
      },
      legend: {
        show: true,
        top: 0,
        textStyle: { fontSize: 12, color: CHART_COLORS.label },
      },
      grid: { ...CHART_GRID, top: 36, left: currency ? 50 : CHART_GRID.left },
      xAxis: {
        ...CHART_X_AXIS_STYLE,
        type: 'category',
        data: categories,
        name: 'Period',
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          ...CHART_X_AXIS_STYLE.axisLabel,
          hideOverlap: true,
        },
      },
      yAxis: {
        ...CHART_Y_AXIS_STYLE,
        type: 'value',
        name: currency ? `Amount (${currency})` : 'Amount',
        nameLocation: 'middle',
        nameGap: currency ? 55 : 45,
        min: 0,
        axisLabel: {
          ...CHART_Y_AXIS_STYLE.axisLabel,
          formatter: currency ? (v: number) => `${currency}${fmt(v)}` : undefined,
        },
      },
      series: [
        ...(pvSeries ? [pvSeries] : []),
        depositSeries,
        {
          type: 'bar',
          name: 'Interest Earned',
          stack: 'total',
          data: interest,
          itemStyle: { color: '#14b8a6', borderRadius: CHART_BAR_BORDER_RADIUS },
          barMaxWidth: explainer ? 40 : undefined,
        },
      ],
    };
  }

  private buildLoanChart(p: AnnuityChartParams): EChartsOption {
    const { pmt, rate, periods, compoundingPerYear = 12, pv = 0, currency = '' } = p;
    const r = rate / (100 * compoundingPerYear);

    const categories: string[] = [];
    const interestData: number[] = [];
    const principalData: number[] = [];
    const balanceData: number[] = [];

    let balance = pv;
    for (let k = 1; k <= periods; k++) {
      categories.push(String(k));
      const interestPayment = balance * r;
      const principalPayment = pmt - interestPayment;
      balance = balance - principalPayment;
      interestData.push(interestPayment);
      principalData.push(principalPayment);
      balanceData.push(Math.max(0, balance));
    }

    return {
      animation: false,
      axisPointer: {
        link: [{ xAxisIndex: 'all' }],
      },
      tooltip: {
        ...CHART_TOOLTIP_STYLE,
        extraCssText: 'box-shadow:0 4px 14px rgba(0,0,0,0.08);border-radius:8px;',
        padding: [10, 14],
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0, 0, 0, 0.04)' } },
        formatter: (tooltipParams) => {
          const items = tooltipParams as Array<{ dataIndex: number }>;
          if (!items.length) {
            return '';
          }
          const idx = items[0].dataIndex;
          const cf = (v: number) => `${currency}${fmt(v)}`;
          return [
            tipHeader(`Period ${categories[idx]}`),
            tipColorRow('#14b8a6', 'Interest', cf(interestData[idx])),
            tipColorRow('#f472b6', 'Principal', cf(principalData[idx])),
            tipDivider(),
            tipSummaryRow('Payment', cf(interestData[idx] + principalData[idx])),
            tipSummaryRow('Balance', cf(balanceData[idx])),
          ].join('');
        },
      },
      legend: {
        show: true,
        top: 0,
        textStyle: { fontSize: 12, color: CHART_COLORS.label },
        data: ['Interest', 'Principal'],
      },
      grid: [
        { left: currency ? 80 : 70, right: CHART_GRID.right, top: 36, bottom: '48%' },
        { left: currency ? 80 : 70, right: CHART_GRID.right, top: '56%', bottom: 44 },
      ],
      xAxis: [
        {
          ...CHART_X_AXIS_STYLE,
          type: 'category',
          data: categories,
          gridIndex: 0,
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false },
        },
        {
          ...CHART_X_AXIS_STYLE,
          type: 'category',
          data: categories,
          gridIndex: 1,
          name: 'Period',
          nameLocation: 'middle',
          nameGap: 28,
          axisLabel: {
            ...CHART_X_AXIS_STYLE.axisLabel,
            hideOverlap: true,
          },
        },
      ],
      yAxis: [
        {
          ...CHART_Y_AXIS_STYLE,
          type: 'value',
          name: currency ? `Payment (${currency})` : 'Payment',
          nameLocation: 'middle',
          nameGap: currency ? 55 : 45,
          gridIndex: 0,
          axisLabel: {
            ...CHART_Y_AXIS_STYLE.axisLabel,
            formatter: currency ? (v: number) => `${currency}${fmt(v)}` : undefined,
          },
        },
        {
          ...CHART_Y_AXIS_STYLE,
          type: 'value',
          name: currency ? `Balance (${currency})` : 'Balance',
          nameLocation: 'middle',
          nameGap: currency ? 55 : 45,
          gridIndex: 1,
          axisLabel: {
            ...CHART_Y_AXIS_STYLE.axisLabel,
            formatter: currency ? (v: number) => `${currency}${fmt(v)}` : undefined,
          },
        },
      ],
      series: [
        {
          type: 'bar',
          name: 'Interest',
          stack: 'payment',
          data: interestData,
          itemStyle: { color: '#14b8a6', borderRadius: [0, 0, 0, 0] },
          xAxisIndex: 0,
          yAxisIndex: 0,
        },
        {
          type: 'bar',
          name: 'Principal',
          stack: 'payment',
          data: principalData,
          itemStyle: { color: '#f472b6', borderRadius: CHART_BAR_BORDER_RADIUS },
          xAxisIndex: 0,
          yAxisIndex: 0,
        },
        {
          type: 'line',
          name: 'Balance',
          data: balanceData,
          symbol: 'none',
          smooth: true,
          lineStyle: { color: '#ec4899', width: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(236, 72, 153, 0.18)' },
              { offset: 1, color: 'rgba(236, 72, 153, 0.02)' },
            ]),
          },
          itemStyle: { color: '#ec4899' },
          xAxisIndex: 1,
          yAxisIndex: 1,
        },
      ],
    };
  }
}
